/**
 * Legacy Book web preview — frontend-only options and readiness helpers.
 */

const STORAGE_PREFIX = 'mlb_legacy_book_preview_';

export const LEGACY_BOOK_THEMES = [
  { id: 'heritage', label: 'Heritage' },
  { id: 'classic', label: 'Classic' },
  { id: 'modern', label: 'Modern' },
];

export const LEGACY_BOOK_COVER_STYLES = [
  { id: 'photo', label: 'Photo cover' },
  { id: 'simple', label: 'Simple cover' },
];

export const DEFAULT_SECTION_TOGGLES = {
  overview: true,
  stories: true,
  voice: true,
  photos: true,
  family: true,
  memorial: true,
};

const DEFAULT_OPTIONS = {
  theme: 'heritage',
  coverStyle: 'photo',
  sections: { ...DEFAULT_SECTION_TOGGLES },
};

function storageKey(personId) {
  return `${STORAGE_PREFIX}${personId || 'unknown'}`;
}

export function loadLegacyBookPreviewOptions(personId) {
  if (!personId) return { ...DEFAULT_OPTIONS, sections: { ...DEFAULT_SECTION_TOGGLES } };
  try {
    const raw = sessionStorage.getItem(storageKey(personId));
    if (!raw) return { ...DEFAULT_OPTIONS, sections: { ...DEFAULT_SECTION_TOGGLES } };
    const parsed = JSON.parse(raw);
    return {
      theme: LEGACY_BOOK_THEMES.some((t) => t.id === parsed.theme) ? parsed.theme : 'heritage',
      coverStyle: LEGACY_BOOK_COVER_STYLES.some((c) => c.id === parsed.coverStyle)
        ? parsed.coverStyle
        : 'photo',
      sections: { ...DEFAULT_SECTION_TOGGLES, ...(parsed.sections || {}) },
    };
  } catch {
    return { ...DEFAULT_OPTIONS, sections: { ...DEFAULT_SECTION_TOGGLES } };
  }
}

export function saveLegacyBookPreviewOptions(personId, options) {
  if (!personId) return;
  try {
    sessionStorage.setItem(storageKey(personId), JSON.stringify(options));
  } catch {
    /* ignore quota errors */
  }
}

export function legacyBookPreviewHref(person) {
  if (!person?.id) return '/dashboard';
  if (person.slug) return `/person/${person.slug}/legacy-book`;
  return `/person/${person.id}/legacy-book`;
}

export function legacyBookPreviewHrefFromId(personId) {
  if (!personId) return '/dashboard';
  return `/person/${personId}/legacy-book`;
}

/** Tree-level HTML Legacy Book preview (Stage 3B). */
export function treeLegacyBookPreviewHref(treeId) {
  if (!treeId) return '/dashboard';
  return `/tree/${treeId}/legacy-book-preview`;
}

export function buildLegacyBookReadiness({ person, stories = [], documents = [], voiceCount = 0, memorial }) {
  const items = [];

  const add = (id, label, done) => items.push({ id, label, done });

  add('photo', 'Add a profile photo', Boolean(person?.profile_image_url));
  add('summary', 'Add a life summary', Boolean((person?.biographical_notes || '').trim()));
  add('stories', 'Add at least 3 stories', (stories?.length || 0) >= 3);
  add('photos', 'Add photos or documents', (documents?.length || 0) > 0);
  add('voice', 'Add voice memories', voiceCount > 0);

  const memorialReady =
    memorial?.status === 'published' ||
    Boolean(memorial?.published_biography?.trim()) ||
    Boolean(person?.memorial_summary?.memorial_active);
  add('memorial', 'Publish or prepare memorial page', memorialReady);

  const complete = items.filter((i) => i.done).length;
  return { items, complete, total: items.length };
}

export function formatRelationshipName(person) {
  if (!person) return 'Unknown';
  return [person.given_names, person.surname].filter(Boolean).join(' ') || 'Unknown';
}
